// src/components/site/SiteHeader.tsx — Site officiel Vanessa
import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X, ExternalLink } from 'lucide-react';
import { NAV, APP_OPEN_URL, type NavGroup } from '@/config/site';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { buttonClasses } from '@/components/ui/button';

const isGroupActive = (group: NavGroup, pathname: string) =>
    group.to ? pathname === group.to : !!group.items?.some((i) => pathname === i.to.split('#')[0]);

const DesktopGroup = ({ group, open, onToggle, onClose }: {
    group: NavGroup; open: boolean; onToggle: () => void; onClose: () => void;
}) => {
    const { pathname } = useLocation();
    const active = isGroupActive(group, pathname);
    const base = `inline-flex items-center gap-1 rounded-full px-3 py-2 text-[15px] font-medium transition-colors ${
        active ? 'bg-brand-tint text-ink' : 'text-gray-700 hover:bg-sand'
    }`;

    if (group.to) {
        return <NavLink to={group.to} className={base} onClick={onClose}>{group.label}</NavLink>;
    }

    return (
        <div className="relative">
            <button
                type="button"
                className={base}
                aria-expanded={open}
                aria-haspopup="true"
                onClick={onToggle}
            >
                {group.label}
                <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
            </button>
            {open && (
                <div className="absolute left-0 top-full mt-2 w-72 rounded-2xl border border-gray-200 bg-white p-2 shadow-lg">
                    {group.items!.map((item) => (
                        <Link
                            key={item.to}
                            to={item.to}
                            onClick={onClose}
                            className="block rounded-xl px-3 py-2.5 hover:bg-sand"
                        >
                            <span className="block text-[15px] font-semibold text-ink">{item.label}</span>
                            {item.description && <span className="block text-sm text-gray-600">{item.description}</span>}
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
};

export const SiteHeader = () => {
    const { isAuthenticated, profile, logout } = useAuth();
    const { pathname } = useLocation();
    const [openGroup, setOpenGroup] = useState<string | null>(null);
    const [mobileOpen, setMobileOpen] = useState(false);
    const navRef = useRef<HTMLDivElement>(null);

    // Fermeture au changement de page, au clic à l'extérieur et avec Échap.
    useEffect(() => {
        setOpenGroup(null);
        setMobileOpen(false);
    }, [pathname]);

    useEffect(() => {
        const onPointer = (e: MouseEvent) => {
            if (navRef.current && !navRef.current.contains(e.target as Node)) setOpenGroup(null);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setOpenGroup(null);
                setMobileOpen(false);
            }
        };
        document.addEventListener('mousedown', onPointer);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onPointer);
            document.removeEventListener('keydown', onKey);
        };
    }, []);

    useEffect(() => {
        document.body.style.overflow = mobileOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [mobileOpen]);

    return (
        <header className="sticky top-0 z-40 border-b border-gray-200 bg-cream/95 backdrop-blur">
            <div className="mx-auto flex h-16 max-w-6xl items-center gap-4 px-4">
                <Link to="/" className="flex shrink-0 items-center gap-2.5" aria-label="Vanessa — accueil">
                    <img src="/vanessa-avatar.png" alt="" className="h-9 w-9 rounded-full object-cover ring-2 ring-brand" />
                    <span className="font-display text-xl font-bold text-ink">Vanessa</span>
                </Link>

                <nav ref={navRef} aria-label="Navigation principale" className="ml-4 hidden items-center gap-1 lg:flex">
                    {NAV.map((group) => (
                        <DesktopGroup
                            key={group.label}
                            group={group}
                            open={openGroup === group.label}
                            onToggle={() => setOpenGroup(openGroup === group.label ? null : group.label)}
                            onClose={() => setOpenGroup(null)}
                        />
                    ))}
                </nav>

                <div className="ml-auto hidden items-center gap-2 lg:flex">
                    {isAuthenticated ? (
                        <>
                            <Link to="/console" className="rounded-full px-3 py-2 text-[15px] font-medium text-gray-700 hover:bg-sand">
                                Console
                            </Link>
                            {profile?.isModerator && (
                                <Link to="/admin" className="rounded-full px-3 py-2 text-[15px] font-medium text-gray-700 hover:bg-sand">
                                    Administration
                                </Link>
                            )}
                            <button
                                type="button"
                                onClick={logout}
                                className="rounded-full px-3 py-2 text-[15px] font-medium text-gray-700 hover:bg-sand"
                            >
                                Déconnexion
                            </button>
                        </>
                    ) : (
                        <Link to="/connexion" className="rounded-full px-3 py-2 text-[15px] font-medium text-gray-700 hover:bg-sand">
                            Connexion
                        </Link>
                    )}
                    <a href={APP_OPEN_URL} className={buttonClasses('primary', 'md')}>
                        Ouvrir l’application
                        <ExternalLink className="w-4 h-4" aria-hidden="true" />
                    </a>
                </div>

                <button
                    type="button"
                    className="ml-auto rounded-full p-2 text-ink hover:bg-sand lg:hidden"
                    aria-label={mobileOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
                    aria-expanded={mobileOpen}
                    onClick={() => setMobileOpen((v) => !v)}
                >
                    {mobileOpen ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
                </button>
            </div>

            {/* Menu mobile */}
            {mobileOpen && (
                <div className="fixed inset-x-0 top-16 bottom-0 overflow-y-auto bg-cream px-4 pb-10 pt-4 lg:hidden">
                    <nav aria-label="Navigation mobile" className="mx-auto max-w-md space-y-5">
                        {NAV.map((group) =>
                            group.to ? (
                                <Link key={group.label} to={group.to} className="block font-display text-xl font-semibold text-ink">
                                    {group.label}
                                </Link>
                            ) : (
                                <div key={group.label}>
                                    <p className="mb-1 text-sm font-semibold uppercase tracking-wide text-gray-600">{group.label}</p>
                                    <ul className="space-y-0.5">
                                        {group.items!.map((item) => (
                                            <li key={item.to}>
                                                <Link to={item.to} className="block rounded-xl px-3 py-2.5 text-lg text-ink hover:bg-sand">
                                                    {item.label}
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )
                        )}

                        <div className="space-y-3 border-t border-gray-200 pt-5">
                            <a href={APP_OPEN_URL} className={buttonClasses('primary', 'lg', 'w-full')}>
                                Ouvrir l’application
                            </a>
                            {isAuthenticated ? (
                                <>
                                    <Link to="/console" className={buttonClasses('ghost', 'lg', 'w-full')}>Console</Link>
                                    <button type="button" onClick={logout} className={buttonClasses('ghost', 'lg', 'w-full')}>
                                        Déconnexion
                                    </button>
                                </>
                            ) : (
                                <Link to="/connexion" className={buttonClasses('ghost', 'lg', 'w-full')}>Connexion</Link>
                            )}
                        </div>
                    </nav>
                </div>
            )}
        </header>
    );
};
